// Semana ISO -> intervalo de datas legível para o denunciante.
//
// POR QUE ISTO EXISTE: a tela mostrava `createdWeek` cru — "2026-W33" — que é
// notação de máquina numa tela lida por quem acabou de denunciar assédio. O
// intervalo ("10 a 16 de agosto de 2026") carrega EXATAMENTE a mesma informação,
// em português.
//
// 🔴 NÃO É AFROUXAMENTO DE ANONIMATO, e a distinção importa: a granularidade
// semanal do lado do denunciante é deliberada (LT-01 / LT-26 — o `caseId` decodificava
// para o milissegundo do envio e isso foi fechado). Esta função converte NOTAÇÃO,
// não granularidade: entra uma semana, sai a mesma semana escrita por extenso.
// Nunca derive hora, dia do envio, nem "há X dias" a partir daqui — seria
// reintroduzir precisão que o sistema tirou de propósito.
//
// Determinística por construção: só aritmética UTC sobre o rótulo recebido, sem
// `Date.now()` e sem fuso local (o fuso da máquina do denunciante já foi vetor de
// correlação uma vez, no CreationDate do PDF — LT-10).

/** Segunda-feira da semana ISO, em UTC. ISO 8601: a semana 1 é a que contém 4 de janeiro. */
function segundaDaSemanaIso(ano: number, semana: number): Date {
  const jan4 = new Date(Date.UTC(ano, 0, 4));
  // getUTCDay(): 0=domingo. ISO conta 1=segunda..7=domingo.
  const diaIso = jan4.getUTCDay() === 0 ? 7 : jan4.getUTCDay();
  const segundaDaSemana1 = Date.UTC(ano, 0, 4 - (diaIso - 1));
  return new Date(segundaDaSemana1 + (semana - 1) * 7 * 86_400_000);
}

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const;

/**
 * Converte `createdWeek` ("2026-W33") no intervalo por extenso
 * ("10 a 16 de agosto de 2026").
 *
 * Entrada malformada volta CRUA, de propósito: um rótulo estranho na tela é
 * visível e investigável; uma data inventada a partir de lixo seria o "fallback
 * plausível que esconde ausência" do CLAUDE.md — indistinguível de um dado bom.
 */
export function intervaloDaSemana(createdWeek: string): string {
  const m = /^(\d{4})-W(\d{2})$/.exec(createdWeek.trim());
  if (!m) return createdWeek;

  const ano = Number(m[1]);
  const semana = Number(m[2]);
  if (semana < 1 || semana > 53) return createdWeek;

  const inicio = segundaDaSemanaIso(ano, semana);
  const fim = new Date(inicio.getTime() + 6 * 86_400_000);

  const d1 = inicio.getUTCDate();
  const d2 = fim.getUTCDate();
  const mes1 = MESES[inicio.getUTCMonth()];
  const mes2 = MESES[fim.getUTCMonth()];
  const ano1 = inicio.getUTCFullYear();
  const ano2 = fim.getUTCFullYear();

  // Semana virando o ano (a ISO-1 costuma começar em dezembro do ano anterior).
  if (ano1 !== ano2) return `${d1} de ${mes1} de ${ano1} a ${d2} de ${mes2} de ${ano2}`;
  // Semana virando o mês.
  if (mes1 !== mes2) return `${d1} de ${mes1} a ${d2} de ${mes2} de ${ano2}`;
  return `${d1} a ${d2} de ${mes2} de ${ano2}`;
}
